import { motion } from 'framer-motion';
import { Factory, Target, Globe, Lightbulb, Users, TrendingUp } from 'lucide-react';

const About = () => {
  const timeline = [
    { year: '2010', event: 'Founded', description: 'LMR IMPEX established with vision for quality manufacturing' },
    { year: '2014', event: 'Facility Expansion', description: 'Invested in automated CNC machinery and PU finishing' },
    { year: '2018', event: 'Export Division', description: 'Launched international export operations for coconut products' },
    { year: '2022', event: 'Global Presence', description: 'Expanded to serve 25+ countries worldwide' }
  ];

  const values = [
    { icon: Target, title: 'Precision Engineering', description: 'Every product meets exacting standards through rigorous quality control.' },
    { icon: Globe, title: 'Global Perspective', description: 'We think globally, understanding international markets and buyer needs.' },
    { icon: Lightbulb, title: 'Innovation First', description: 'Continuous investment in automation and modern manufacturing technology.' },
    { icon: Users, title: 'Customer Focus', description: 'Building lasting partnerships through reliability and excellence.' }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="about-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">About LMR IMPEX</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Engineering Precision.<br />
              Global Reach.<br />
              Curated Luxury.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              We are a vertically integrated manufacturing and trading company operating across furniture production, 
              luxury interiors, strategic imports, and international exports. Based in India, serving the world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Journey */}
      <section className="py-24 md:py-32" data-testid="company-journey">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Story</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                From Local Craftsmanship to Global Manufacturing
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-4">
                LMR IMPEX began with a simple mission: to manufacture furniture with uncompromising quality. 
                Over the years, we've evolved into a comprehensive solutions provider across multiple verticals.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground mb-4">
                Today, our state-of-the-art facility houses automated CNC machines, panel saws, edge banders, 
                and a dedicated PU painting chamber. We combine traditional craftsmanship with modern technology 
                to deliver products that meet international standards.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our export division serves global buyers with premium coconut fibre and coco peat, while our 
                interior division creates stunning residential and commercial spaces across India.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 lg:h-full"
            >
              <img 
                src="https://images.pexels.com/photos/35383622/pexels-photo-35383622.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                alt="Factory Worker"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="company-values">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Values</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              What Drives Us Forward
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 transition-all duration-500 hover:shadow-lg"
                data-testid={`value-${index}`}
              >
                <value.icon className="w-10 h-10 text-bronze-500 mb-4" />
                <h3 className="text-xl font-medium mb-3">{value.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32" data-testid="company-timeline">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Journey</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Growth Through Innovation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
                data-testid={`timeline-${index}`}
              >
                <div className="text-5xl font-bold text-bronze-500 mb-2">{item.year}</div>
                <h3 className="text-xl font-medium mb-2">{item.event}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* China Partnership */}
      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="china-partnership">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Factory className="w-16 h-16 text-bronze-500 mb-6" />
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Strategic Sourcing from China
              </h2>
              <p className="text-base leading-relaxed text-white/80 mb-4">
                We maintain strong partnerships with trusted manufacturers in China, allowing us to source 
                premium furniture components and materials at competitive prices.
              </p>
              <p className="text-base leading-relaxed text-white/80">
                Every imported product undergoes rigorous quality inspection before reaching our facility, 
                ensuring that only the finest materials make it to our customers.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-white/10 border border-white/20 p-6">
                <div className="text-4xl font-bold text-bronze-500 mb-2">100%</div>
                <p className="text-sm text-white/80">Quality Inspected</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-6">
                <div className="text-4xl font-bold text-bronze-500 mb-2">50+</div>
                <p className="text-sm text-white/80">Partner Manufacturers</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-6">
                <div className="text-4xl font-bold text-bronze-500 mb-2">24/7</div>
                <p className="text-sm text-white/80">Supply Chain Monitoring</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-6">
                <div className="text-4xl font-bold text-bronze-500 mb-2">10+</div>
                <p className="text-sm text-white/80">Years Partnership</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section Placeholder */}
      <section className="py-24 md:py-32" data-testid="leadership-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Leadership</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Guided by Experience
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mt-6 max-w-3xl mx-auto">
              Our leadership team brings decades of combined experience in manufacturing, international trade, 
              and design excellence.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
